import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, Platform } from 'react-native';
import { Button, TextInput, Text, useTheme, Switch, HelperText, Snackbar, ActivityIndicator, Portal, Modal } from 'react-native-paper';
import { modeloMensagemService } from '../../services/modeloMensagemService';
import { supabase } from '../../services/supabase';

type ModeloMensagem = {
  id?: number;
  codempresa: number;
  modelo: string;
  n8n: boolean;
};

type Props = {
  route: any;
  navigation: any;
};

export function ModeloForm({ route, navigation }: Props) {
  const { modelo: modeloParam } = route.params || {};
  const isEditing = !!modeloParam?.id;
  
  const [modelo, setModelo] = useState<ModeloMensagem>({
    codempresa: 0,
    modelo: '',
    n8n: false,
    ...modeloParam
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showTemplateVars, setShowTemplateVars] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const theme = useTheme();
  
  // Estado para as variáveis da empresa
  const [empresaVars, setEmpresaVars] = useState({
    nome: '',
    cnpj: '',
    telefone: ''
  });
  
  useEffect(() => {
    if (isEditing) {
      setModelo(modeloParam);
    }
  }, [isEditing, modeloParam]);

  const templateVars = {
    n8n: [
      '[cliente]', '[codcli]', '[cgc]', '[valordeb]', 
      '[qtdetitul]', '[mindtvenc]', '[maxdtvenc]', '[titulos]'
    ],
    individual: [
      '[cliente]', '[duplic]', '[valortitulo]', 
      '[dtvenc]', '[linhadig]'
    ]
  };

  useEffect(() => {
    // Se estiver editando, definir o título da tela
    if (isEditing) {
      navigation.setOptions({ title: 'Editar Modelo' });
    } else {
      navigation.setOptions({ title: 'Novo Modelo' });
    }
  }, [isEditing, navigation]);

  const handleSave = async () => {
    if (!modelo.modelo.trim()) {
      setError('O modelo não pode estar vazio');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Buscar o codempresa do usuário (sempre, para garantir)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('empresa')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.empresa) throw new Error('Empresa não encontrada');

      // Preparar o modelo com o codempresa correto
      const modeloParaSalvar = {
        modelo: modelo.modelo,
        n8n: modelo.n8n,
        codempresa: userData.empresa // Sempre usa o codempresa do usuário atual
      };

      // Salva o modelo
      if (isEditing && modelo.id) {
        await modeloMensagemService.updateModelo(modelo.id, modeloParaSalvar);
        setSuccess('Modelo atualizado com sucesso!');
      } else {
        await modeloMensagemService.createModelo(modeloParaSalvar);
        setSuccess('Modelo criado com sucesso!');
        
        // Limpa o formulário para um novo modelo
        setModelo({
          ...modelo,
          modelo: '',
          n8n: modelo.n8n // Mantém o tipo selecionado
        });
      }

      // Navegar de volta após 1.5 segundos
      setTimeout(() => {
        navigation.goBack();
      }, 1500);
    } catch (err: any) {
      setError('Erro ao salvar modelo: ' + err.message);
      console.error('Erro ao salvar modelo:', err);
    } finally {
      setLoading(false);
    }
  };

  // Buscar dados da empresa do usuário
  const buscarDadosEmpresa = async () => {
    try {
      // Buscar o ID da empresa do usuário
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('empresa')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.empresa) throw new Error('Empresa não encontrada');

      // Buscar dados da empresa
      const { data: empresaData, error: empresaError } = await supabase
        .from('empresa')
        .select('*')
        .eq('id', userData.empresa)
        .single();

      if (empresaError) throw empresaError;
      if (!empresaData) throw new Error('Dados da empresa não encontrados');

      // Verificar se a empresa tem dados_empresa
      if (empresaData.dados_empresa) {
        setEmpresaVars({
          nome: empresaData.dados_empresa.nome || 'Nome da Empresa',
          cnpj: empresaData.dados_empresa.cnpj || 'XX.XXX.XXX/0001-XX',
          telefone: empresaData.dados_empresa.telefone || '(XX) XXXX-XXXX'
        });
      } else {
        // Se não tiver, usar valores padrão
        setEmpresaVars({
          nome: empresaData.nome || 'Nome da Empresa',
          cnpj: empresaData.cnpj || 'XX.XXX.XXX/0001-XX',
          telefone: empresaData.telefone || '(XX) XXXX-XXXX'
        });
      }

      return userData.empresa;
    } catch (error) {
      console.error('Erro ao buscar dados da empresa:', error);
      // Usar valores padrão em caso de erro
      setEmpresaVars({
        nome: 'Nome da Empresa',
        cnpj: 'XX.XXX.XXX/0001-XX',
        telefone: '(XX) XXXX-XXXX'
      });
      throw error;
    }
  };

  // Abrir modal para editar variáveis da empresa
  const abrirModalVariaveis = async () => {
    try {
      await buscarDadosEmpresa();
      setModalVisible(true);
    } catch (error) {
      console.error('Erro ao abrir modal:', error);
      setError('Não foi possível carregar os dados da empresa');
    }
  };
  
  // Aplicar variáveis ao modelo e salvar imediatamente na tabela empresa
  const aplicarVariaveis = async () => {
    try {
      setLoading(true);
      
      // Buscar o ID da empresa do usuário
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('empresa')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.empresa) throw new Error('Empresa não encontrada');
      
      // Atualizar os dados da empresa na tabela empresa
      const { error: updateError } = await supabase
        .from('empresa')
        .update({ dados_empresa: empresaVars })
        .eq('id', userData.empresa);
      
      if (updateError) throw updateError;
      
      setModalVisible(false);
      setSuccess('Dados da empresa salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar dados da empresa:', error);
      setError('Erro ao salvar dados da empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = async () => {
    try {
      setLoading(true);
      console.log('Buscando modelo padrão...');
      
      // Buscar APENAS o modelo padrão (codempresa = null)
      const { data, error } = await supabase
        .from('modelomensagem')
        .select('*')
        .is('codempresa', null)
        .eq('n8n', modelo.n8n);

      console.log('Resultado da busca:', { data, error });
      
      if (error) {
        console.error('Erro ao buscar modelo padrão:', error);
        setError('Erro ao buscar modelo padrão');
        return;
      }
      
      if (!data || data.length === 0) {
        setError(`Nenhum modelo padrão encontrado para ${modelo.n8n ? 'títulos únicos' : 'títulos múltiplos'}`);
        return;
      }
      
      // Pegar o primeiro modelo encontrado
      const modeloPadrao = data[0];
      console.log('Modelo padrão encontrado:', modeloPadrao);
      
      // Atualiza APENAS o campo modelo
      setModelo(prev => ({
        ...prev,
        modelo: modeloPadrao.modelo
      }));
      
      // Atualiza o modelo com os dados do modelo padrão
      setModelo(prev => ({
        ...prev,
        modelo: modeloPadrao.modelo
      }));
      
      // Buscar dados da empresa e abrir o modal
      await buscarDadosEmpresa();
      setModalVisible(true);
      
      setSuccess('Modelo padrão importado com sucesso!');


      
    } catch (error) {
      console.error('Erro ao carregar modelo padrão:', error);
      setError('Erro ao carregar o modelo padrão');
    } finally {
      setLoading(false);
    }
  };

  const insertVariable = (variable: string) => {
    setModelo({
      ...modelo,
      modelo: `${modelo.modelo}${variable}`
    });
  };

  return (
    <KeyboardAvoidingView 
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={100}
    >
      <ScrollView style={styles.scrollView}>
        <View style={styles.switchContainer}>
          <Text style={styles.switchLabel}>
            {modelo.n8n ? 'Vários Títulos (N8N)' : 'Título Único'}
          </Text>
          <Switch
            value={modelo.n8n}
            onValueChange={(value) => setModelo({ ...modelo, n8n: value })}
            disabled={isEditing}
          />
        </View>

        <HelperText type="info" style={styles.helperText}>
          {modelo.n8n 
            ? 'Modelo para envio de vários títulos (N8N)'
            : 'Modelo para envio de um único título'}
        </HelperText>

        <View style={styles.buttonContainer}>
          <Button 
            mode="outlined" 
            onPress={handleUseTemplate}
            style={styles.templateButton}
          >
            Usar Modelo Padrão
          </Button>
          
          <Button 
            mode="outlined" 
            onPress={() => setShowTemplateVars(!showTemplateVars)}
            style={styles.templateButton}
          >
            {showTemplateVars ? 'Ocultar Variáveis' : 'Mostrar Variáveis'}
          </Button>
        </View>

        {showTemplateVars && (
          <View style={styles.varsContainer}>
            <Text style={styles.varsTitle}>
              Variáveis disponíveis ({modelo.n8n ? 'Vários Títulos (N8N)' : 'Título Único'}):
            </Text>
            <View style={styles.varsGrid}>
              {(modelo.n8n ? templateVars.n8n : templateVars.individual).map((variable) => (
                <Button
                  key={variable}
                  mode="outlined"
                  compact
                  onPress={() => insertVariable(variable)}
                  style={styles.varButton}
                >
                  {variable}
                </Button>
              ))}
            </View>
            <HelperText type="info" style={styles.varsHelper}>
              Clique em uma variável para inserir no modelo
            </HelperText>
          </View>
        )}

        <TextInput
          label="Modelo de Mensagem"
          value={modelo.modelo}
          onChangeText={(text) => setModelo({ ...modelo, modelo: text })}
          multiline
          numberOfLines={10}
          style={styles.textInput}
          scrollEnabled={true}
          textAlignVertical="top"
        />

        <View style={styles.previewContainer}>
          <Button 
            icon={showPreview ? 'eye-off' : 'eye'}
            onPress={() => setShowPreview(!showPreview)}
            style={styles.previewButton}
          >
            {showPreview ? 'Ocultar Visualização' : 'Visualizar Mensagem'}
          </Button>
          
          {showPreview && (
            <View style={styles.previewBox}>
              <Text style={styles.previewText}>
                {modelo.modelo || '(Nenhum conteúdo para visualizar)'}
              </Text>
            </View>
          )}
        </View>

        {error && (
          <Text style={[styles.errorText, { color: theme.colors.error }]}>
            {error}
          </Text>
        )}

        <Button
          mode="contained"
          onPress={handleSave}
          style={styles.saveButton}
          disabled={loading || !modelo.modelo}
        >
          {isEditing ? 'Atualizar Modelo' : 'Salvar Modelo'}
        </Button>
      </ScrollView>

      <Portal>
        {/* Modal para edição das variáveis da empresa */}
        <Modal
          visible={modalVisible}
          onDismiss={() => setModalVisible(false)}
          contentContainerStyle={styles.modalContainer}
        >
          <Text style={styles.modalTitle}>Editar Variáveis da Empresa</Text>
          
          <Text style={styles.modalSubtitle}>Estas variáveis serão substituídas no modelo:</Text>
          
          <TextInput
            label="Nome da Empresa [empresanome]"
            value={empresaVars.nome}
            onChangeText={(text) => setEmpresaVars(prev => ({ ...prev, nome: text }))}
            style={styles.input}
            mode="outlined"
          />
          
          <TextInput
            label="CNPJ [empresacnpj]"
            value={empresaVars.cnpj}
            onChangeText={(text) => setEmpresaVars(prev => ({ ...prev, cnpj: text }))}
            style={styles.input}
            mode="outlined"
          />
          
          <TextInput
            label="Telefone [empresatel]"
            value={empresaVars.telefone}
            onChangeText={(text) => setEmpresaVars(prev => ({ ...prev, telefone: text }))}
            style={styles.input}
            mode="outlined"
          />
          
          <View style={styles.modalButtonContainer}>
            <Button 
              mode="outlined" 
              onPress={() => setModalVisible(false)}
              style={styles.modalButton}
            >
              Cancelar
            </Button>
            
            <Button 
              mode="contained" 
              onPress={aplicarVariaveis}
              style={styles.modalButton}
            >
              Aplicar
            </Button>
          </View>
        </Modal>

        <Snackbar
          visible={!!error}
          onDismiss={() => setError(null)}
          duration={3000}
          style={{ backgroundColor: '#F44336' }}
        >
          {error}
        </Snackbar>

        <Snackbar
          visible={!!success}
          onDismiss={() => setSuccess(null)}
          duration={3000}
          style={{ backgroundColor: '#4CAF50' }}
        >
          {success}
        </Snackbar>
      </Portal>

      <Portal>
        <Modal visible={loading} contentContainerStyle={styles.loadingModal}>
          <View style={styles.loadingContent}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={styles.loadingText}>
              {isEditing ? 'Atualizando modelo...' : 'Salvando modelo...'}
            </Text>
          </View>
        </Modal>
      </Portal>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  scrollView: {
    flex: 1,
    padding: 16,
  },
  // Estilos para o modal de variáveis
  modalContainer: {
    backgroundColor: '#fff',
    padding: 20,
    margin: 20,
    borderRadius: 10,
    elevation: 5,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  modalSubtitle: {
    fontSize: 14,
    marginBottom: 20,
    color: '#666',
    textAlign: 'center',
  },
  input: {
    marginBottom: 15,
  },
  modalButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  modalButton: {
    flex: 1,
    marginHorizontal: 5,
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    paddingHorizontal: 8,
  },
  switchLabel: {
    fontSize: 16,
    fontWeight: '500',
  },
  helperText: {
    marginBottom: 16,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  templateButton: {
    marginBottom: 8,
    minWidth: '48%',
  },
  varsContainer: {
    marginBottom: 16,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 12,
  },
  varsTitle: {
    fontWeight: '500',
    marginBottom: 8,
  },
  varsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  varButton: {
    margin: 4,
  },
  varsHelper: {
    marginTop: 0,
  },
  textInput: {
    marginBottom: 16,
    minHeight: 200,
    textAlign: 'left',
  },
  previewContainer: {
    marginBottom: 24,
  },
  previewButton: {
    marginBottom: 8,
  },
  previewBox: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    padding: 12,
    backgroundColor: '#f9f9f9',
    minHeight: 100,
  },
  previewText: {
    color: '#333',
    lineHeight: 22,
  },
  errorText: {
    marginBottom: 16,
    textAlign: 'center',
  },
  saveButton: {
    marginTop: 8,
    marginBottom: 24,
    paddingVertical: 6,
  },
  loadingModal: {
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingContent: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 8,
    alignItems: 'center',
    elevation: 4,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
  },
});
